import { useNavigate, useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PhotoComparisonSlider } from '@/components/PhotoComparisonSlider';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { features, getFeatureBySlug } from '@/lib/featuresData';
import { Reveal } from '@/components/Reveal';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Heart,
  Zap,
  BookOpen,
  Layers,
  HelpCircle,
  Trophy,
} from 'lucide-react';

export default function FeatureDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const feature = slug ? getFeatureBySlug(slug) : undefined;

  if (!feature) {
    return (
      <Layout>
        <PageMeta title="Feature not found – ListBright" description="The requested feature does not exist." />
        <section className="container max-w-2xl mx-auto px-4 py-24 text-center space-y-6">
          <h1 className="text-3xl font-display font-bold">Feature not found</h1>
          <p className="text-muted-foreground">We couldn't find that feature. Browse all features below.</p>
          <Button onClick={() => navigate('/features')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> All features
          </Button>
        </section>
      </Layout>
    );
  }

  const Icon = feature.icon;
  const hasComparison = feature.hasComparison !== false && feature.beforeImage && feature.afterImage;
  const hasSampleVideo = !!feature.sampleVideo;

  const currentIndex = features.findIndex((f) => f.id === feature.id);
  const prev = currentIndex > 0 ? features[currentIndex - 1] : undefined;
  const next = currentIndex < features.length - 1 ? features[currentIndex + 1] : undefined;

  const seoTitle = feature.seoTitle ?? `${feature.title} – ListBright Features`;
  const seoDescription = feature.seoDescription ?? feature.tagline;

  const jsonLd: Record<string, unknown>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: feature.title,
      description: seoDescription,
      provider: { '@type': 'Organization', name: 'ListBright', url: 'https://listbright.ai' },
      areaServed: 'Worldwide',
      serviceType: 'Real estate listing AI',
    },
  ];
  if (feature.faqs && feature.faqs.length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: feature.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  return (
    <Layout>
      <PageMeta
        title={seoTitle}
        description={seoDescription}
        breadcrumbs={[
          { label: 'Features', path: '/features' },
          { label: feature.title, path: `/features/${feature.id}` },
        ]}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16 lg:py-20 border-b border-border/40">
        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute top-[-6rem] right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="container max-w-5xl mx-auto px-4 relative">
          <Link
            to="/features"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All features
          </Link>
          <div className="flex items-center gap-3 mb-4 animate-fade-in">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <Badge variant="outline">{feature.tagline}</Badge>
          </div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold mb-4 tracking-tight leading-[1.05] animate-fade-up">{feature.title}</h1>
          <p className="text-lg text-muted-foreground max-w-3xl animate-fade-up" style={{ animationDelay: '80ms' }}>{feature.description}</p>
          {feature.stats && feature.stats.length > 0 && (
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8 max-w-3xl">
              {feature.stats.map((s, i) => (
                <Reveal
                  as="div"
                  key={s.label}
                  delay={i * 80}
                  className="card-premium rounded-2xl border border-border/40 bg-background/60 backdrop-blur-sm p-4"
                >
                  <dt className="text-2xl font-display font-bold text-primary">{s.value}</dt>
                  <dd className="text-xs sm:text-sm text-muted-foreground mt-1">{s.label}</dd>
                </Reveal>
              ))}
            </dl>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Button variant="hero" size="lg" onClick={() => navigate('/?create=true')} className="shadow-lg">
              <Zap className="w-4 h-4 mr-2" /> Try {feature.title} free
            </Button>
            <Button variant="outline" size="lg" onClick={() => navigate('/pricing')}>
              See pricing <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Example */}
      <section className="py-14 lg:py-20">
        <div className="container max-w-5xl mx-auto px-4 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider">
            <Sparkles className="w-4 h-4 text-primary" /> Example
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold">See it in action</h2>
          {hasSampleVideo ? (
            <div className="space-y-5 mt-4">
              <div className="rounded-2xl overflow-hidden shadow-elevated border border-border/40 bg-black">
                <video
                  src={feature.sampleVideo}
                  key={feature.sampleVideo}
                  controls
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full aspect-video object-cover"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              {feature.sampleVideoSourcePhotos && feature.sampleVideoSourcePhotos.length > 0 && (
                <div className="rounded-2xl border border-border/40 bg-muted/30 p-4 sm:p-5">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      Photos used to create this video
                    </p>
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      AI generates one cinematic clip per photo, then stitches them together.
                    </span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
                    {feature.sampleVideoSourcePhotos.map((p) => (
                      <figure key={p.src} className="space-y-1.5">
                        <div className="aspect-[4/3] rounded-lg overflow-hidden border border-border/40 bg-muted">
                          <img
                            src={p.src}
                            alt={p.label}
                            loading="lazy"
                            className="w-full h-full object-cover"
                            width={400}
                            height={300}
                          />
                        </div>
                        <figcaption className="text-[10px] sm:text-xs text-muted-foreground text-center truncate">
                          {p.label}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : hasComparison ? (
            <div className="rounded-2xl overflow-hidden shadow-elevated border border-border/40 mt-4">
              <PhotoComparisonSlider
                originalSrc={feature.beforeImage!}
                enhancedSrc={feature.afterImage!}
                className="aspect-[4/3]"
              />
            </div>
          ) : (
            <div className="rounded-2xl bg-gradient-to-br from-primary/8 via-accent/5 to-muted p-12 border border-border/40 mt-4">
              <div className="aspect-[4/3] flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Icon className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">{feature.title}</h3>
                <p className="text-sm text-muted-foreground max-w-md">{feature.tagline}</p>
              </div>
            </div>
          )}

          {/* Key benefits */}
          <ul className="grid sm:grid-cols-2 gap-3 pt-6">
            {feature.benefits.map((b, i) => (
              <Reveal as="li" key={b} delay={(i % 2) * 70} className="flex items-start gap-3 p-4 rounded-xl bg-muted/40 border border-border/40 transition-colors hover:border-primary/25">
                <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <span className="text-sm">{b}</span>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Why it helps */}
      <section className="bg-card border-y border-border/40 py-14 lg:py-20">
        <div className="container max-w-5xl mx-auto px-4 space-y-6">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider">
            <Heart className="w-4 h-4 text-primary" /> Why it helps
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold max-w-2xl">
            Why this matters for your listing
          </h2>
          <div className="grid md:grid-cols-3 gap-4 pt-2">
            {feature.whyItHelps.map((point, i) => (
              <Reveal
                as="div"
                key={i}
                delay={i * 90}
                className="card-premium p-6 rounded-2xl bg-background border border-border/40 space-y-3"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold">
                  {i + 1}
                </div>
                <p className="text-sm leading-relaxed text-foreground">{point}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Selling impact */}
      <section className="py-14 lg:py-20">
        <div className="container max-w-5xl mx-auto px-4 space-y-6">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider">
            <TrendingUp className="w-4 h-4 text-primary" /> Selling impact
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold max-w-2xl">
            How it helps you sell faster — and for more
          </h2>
          <ul className="space-y-3 pt-2">
            {feature.sellingImpact.map((point, i) => (
              <Reveal
                as="li"
                key={i}
                delay={i * 70}
                className="flex items-start gap-4 p-5 rounded-xl bg-gradient-to-r from-primary/5 to-transparent border border-primary/10 transition-colors hover:border-primary/25"
              >
                <TrendingUp className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <span className="text-foreground">{point}</span>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Deep dive (long-form SEO content) */}
      {feature.longDescription && feature.longDescription.length > 0 && (
        <section className="bg-card border-y border-border/40 py-14 lg:py-20">
          <div className="container max-w-3xl mx-auto px-4 space-y-6">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider">
              <BookOpen className="w-4 h-4 text-primary" /> Deep dive
            </div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold">
              How {feature.title.toLowerCase()} works for real estate
            </h2>
            <div className="space-y-5 pt-2">
              {feature.longDescription.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Use cases */}
      {feature.useCases && feature.useCases.length > 0 && (
        <section className="py-14 lg:py-20">
          <div className="container max-w-5xl mx-auto px-4 space-y-6">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider">
              <Layers className="w-4 h-4 text-primary" /> Use cases
            </div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold max-w-2xl">
              Where agents and owners use {feature.title}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              {feature.useCases.map((u, i) => (
                <Reveal
                  as="div"
                  key={u.title}
                  delay={(i % 2) * 80}
                  className="card-premium p-6 rounded-2xl border border-border/40 bg-background space-y-2"
                >
                  <h3 className="font-semibold text-foreground">{u.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{u.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {feature.faqs && feature.faqs.length > 0 && (
        <section className="bg-card border-y border-border/40 py-14 lg:py-20">
          <div className="container max-w-3xl mx-auto px-4 space-y-6">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider">
              <HelpCircle className="w-4 h-4 text-primary" /> FAQ
            </div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="w-full pt-2">
              {feature.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="pb-20">
        {feature.id === 'virtual-staging' && (
          <div className="container max-w-4xl mx-auto px-4 mb-14">
            <Link
              to="/best-virtual-staging-software"
              className="group relative block overflow-hidden rounded-3xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-8 sm:p-10 shadow-elevated hover:border-primary hover:shadow-xl transition-all"
            >
              <div className="absolute -top-3 left-8">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 shadow-lg shadow-primary/30 uppercase tracking-widest">
                  <Trophy className="w-3.5 h-3.5" />
                  Editor's Pick
                </span>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <div className="shrink-0 w-16 h-16 rounded-2xl bg-primary/15 flex items-center justify-center">
                  <Trophy className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-primary font-bold mb-2">
                    Independent comparison · Updated 2026
                  </div>
                  <h3 className="font-display font-bold text-2xl sm:text-3xl mb-2 leading-tight">
                    Best Virtual Staging Software (2026)
                  </h3>
                  <p className="text-base text-muted-foreground mb-4">
                    See how ListBright stacks up against BoxBrownie, Virtual Staging AI, ApplyDesign, Styldod and 4 more — ranked on quality, speed, price and what you actually get beyond staging.
                  </p>
                  <span className="inline-flex items-center gap-2 font-semibold text-primary group-hover:gap-3 transition-all">
                    Read the full comparison
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        )}
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="text-center space-y-6 p-10 sm:p-14 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20">
            <h2 className="text-3xl font-display font-bold">
              {feature.ctaHeadline ?? `Try ${feature.title} on your listing`}
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {feature.ctaSubtext ?? 'Create a listing in minutes and see the difference for yourself.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button variant="hero" size="lg" onClick={() => navigate('/?create=true')} className="text-lg px-8 py-6">
                <Zap className="w-5 h-5 mr-2" />
                Create Your Listing
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/pricing')} className="text-lg px-6 py-6">
                See Pricing
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              No credit card required · First listing free · Ready in ~10 minutes
            </p>
          </Reveal>
        </div>
      </section>

      {/* Prev / Next */}
      <section className="pb-20">
        <div className="container max-w-5xl mx-auto px-4 grid sm:grid-cols-2 gap-4">
          {prev ? (
            <Link
              to={`/features/${prev.id}`}
              className="group p-5 rounded-2xl border border-border/40 hover:border-primary/40 hover:bg-muted/30 transition-colors flex items-center gap-3"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Previous</div>
                <div className="font-semibold">{prev.title}</div>
              </div>
            </Link>
          ) : (
            <div />
          )}
          {next ? (
            <Link
              to={`/features/${next.id}`}
              className="group p-5 rounded-2xl border border-border/40 hover:border-primary/40 hover:bg-muted/30 transition-colors flex items-center gap-3 sm:justify-end sm:text-right"
            >
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Next</div>
                <div className="font-semibold">{next.title}</div>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </Link>
          ) : (
            <div />
          )}
        </div>
      </section>
    </Layout>
  );
}